import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { toast } from 'sonner';
import { 
  HardDrive, 
  Cloud, 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  Eye, 
  EyeOff, 
  Save,
  Edit2,
  Lock,
  ChevronDown,
  ChevronUp,
  Plug
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsStorage = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isOpen, setIsOpen] = useState(true); // Start expanded
  const [settings, setSettings] = useState({
    endpoint: '',
    region: '',
    access_key_id: '',
    secret_access_key: '',
    bucket_name: '',
    is_active: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/storage-settings/global`, getAuthHeaders());
      if (res.data && res.data.endpoint) {
        setSettings({
          endpoint: res.data.endpoint || '',
          region: res.data.region || '',
          access_key_id: res.data.access_key_id || '',
          secret_access_key: '', // Never returned from API for security
          bucket_name: res.data.bucket_name || '',
          is_active: res.data.is_active ?? true
        });
        setIsConfigured(true);
        setIsEditing(false);
        // Keep open so user can see the locked state with Edit/Test buttons
      } else {
        setIsConfigured(false);
        setIsEditing(true);
      }
    } catch (error) {
      // No settings configured yet
      setIsConfigured(false);
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    // For testing when locked, we need to use the stored credentials
    const testData = isEditing ? settings : {
      ...settings,
      secret_access_key: settings.secret_access_key || 'USE_STORED' // Backend should use stored key
    };

    if (!testData.endpoint || !testData.access_key_id || !testData.bucket_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // If editing and no secret key, require it
    if (isEditing && !settings.secret_access_key) {
      toast.error('Secret Access Key is required to test connection');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API_URL}/storage-settings/test`, testData, getAuthHeaders());
      setTestResult(res.data);
      if (res.data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error(res.data.message || 'Connection failed');
      }
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Connection failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!settings.endpoint || !settings.access_key_id || !settings.bucket_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Require secret key for new config
    if (!isConfigured && !settings.secret_access_key) {
      toast.error('Secret Access Key is required');
      return;
    }
    
    setSaving(true);
    try {
      const saveData = { ...settings };
      // If updating and no new secret provided, remove it so backend keeps existing
      if (isConfigured && !saveData.secret_access_key) {
        delete saveData.secret_access_key;
      }
      
      await axios.post(`${API_URL}/storage-settings/global`, saveData, getAuthHeaders());
      toast.success('Storage settings saved successfully');
      setSettings(prev => ({ ...prev, secret_access_key: '' }));
      setIsConfigured(true);
      setIsEditing(false);
      setIsOpen(false); // Collapse and lock on save
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Storage Settings
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Configure iDrive e2 cloud storage for file management
        </p>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">e2</span>
                </div>
                <div>
                  <CardTitle className="text-lg">iDrive e2 Configuration</CardTitle>
                  <CardDescription>S3-compatible cloud storage</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isConfigured && !isEditing && (
                  <span className="flex items-center gap-1 text-sm text-green-600 bg-green-50 px-2 py-1 rounded-full">
                    <CheckCircle2 size={14} />
                    Configured
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open('https://console.idrivee2.com/partner/a2gdesigns', '_blank')}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  iDrive Console →
                </Button>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm">
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>
          </CardHeader>
          
          <CollapsibleContent>
            <CardContent>
              {/* Locked State */}
              {isConfigured && !isEditing && (
                <div className="space-y-4">
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Lock size={18} className="text-slate-500" />
                      <div>
                        <p className="font-medium text-slate-900">Storage Settings Locked</p>
                        <p className="text-sm text-slate-500">
                          Endpoint: {settings.endpoint} | Bucket: {settings.bucket_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleTestConnection}
                        disabled={testing}
                      >
                        {testing ? (
                          <Loader2 size={14} className="mr-2 animate-spin" />
                        ) : (
                          <Plug size={14} className="mr-2" />
                        )}
                        Test Connection
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setIsEditing(true)}
                      >
                        <Edit2 size={14} className="mr-2" />
                        Edit
                      </Button>
                    </div>
                  </div>

                  {/* Test Result in Locked State */}
                  {testResult && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {testResult.success ? (
                        <CheckCircle2 size={18} />
                      ) : (
                        <XCircle size={18} />
                      )}
                      <span className="text-sm font-medium">{testResult.message}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Edit Form */}
              {(isEditing || !isConfigured) && (
                <form onSubmit={handleSave} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Endpoint *</Label>
                      <Input
                        value={settings.endpoint}
                        onChange={(e) => setSettings({ ...settings, endpoint: e.target.value })}
                        placeholder="s3.us-central-1.idrivee2.com"
                        data-testid="storage-endpoint-input"
                      />
                      <p className="text-xs text-slate-400">Without https://</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Region *</Label>
                      <Input
                        value={settings.region}
                        onChange={(e) => setSettings({ ...settings, region: e.target.value })}
                        placeholder="us-central-1"
                        data-testid="storage-region-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Access Key ID *</Label>
                      <Input
                        value={settings.access_key_id}
                        onChange={(e) => setSettings({ ...settings, access_key_id: e.target.value })}
                        placeholder="Your access key"
                        data-testid="storage-access-key-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Secret Access Key {isConfigured ? '(leave blank to keep current)' : '*'}</Label>
                      <div className="relative">
                        <Input
                          type={showSecret ? 'text' : 'password'}
                          value={settings.secret_access_key}
                          onChange={(e) => setSettings({ ...settings, secret_access_key: e.target.value })}
                          placeholder={isConfigured ? '••••••••' : 'Enter secret key'}
                          data-testid="storage-secret-key-input"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                          onClick={() => setShowSecret(!showSecret)}
                        >
                          {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Bucket Name *</Label>
                    <Input
                      value={settings.bucket_name}
                      onChange={(e) => setSettings({ ...settings, bucket_name: e.target.value })}
                      placeholder="your-bucket-name"
                      data-testid="storage-bucket-input"
                    />
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                    <Switch
                      checked={settings.is_active}
                      onCheckedChange={(checked) => setSettings({ ...settings, is_active: checked })}
                      id="storage-active"
                    />
                    <Label htmlFor="storage-active" className="cursor-pointer">
                      Enable cloud storage
                    </Label>
                  </div>

                  {/* Test Result in Edit Mode */}
                  {testResult && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {testResult.success ? (
                        <CheckCircle2 size={18} />
                      ) : (
                        <XCircle size={18} />
                      )}
                      <span className="text-sm font-medium">{testResult.message}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleTestConnection}
                      disabled={testing}
                      data-testid="test-storage-btn"
                    >
                      {testing ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <Plug size={16} className="mr-2" />
                          Test Connection
                        </>
                      )}
                    </Button>
                    {isConfigured && isEditing && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false);
                          fetchSettings();
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      type="submit"
                      className="bg-slate-900 hover:bg-slate-800"
                      disabled={saving}
                      data-testid="save-storage-btn"
                    >
                      {saving ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={16} className="mr-2" />
                          Save & Close
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-100 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Cloud className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Folder Structure</p>
              <p className="text-blue-600">
                When you create a new tenant, a folder is automatically created. Client folders are added within tenant folders when clients are created.
              </p>
              <pre className="mt-2 bg-blue-100/50 p-2 rounded text-xs">
{`bucket/
├── tenants/
│   ├── {tenant-id}/
│   │   ├── logo/
│   │   ├── clients/
│   │   │   ├── {client-id}/
│   │   │   │   ├── documents/
│   │   │   │   └── projects/`}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsStorage;
